import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, Observable, of, throwError } from 'rxjs';
import { HttpGenericService } from '../../api/http-generic.service';
import {
  AseguradoCliente,
  AuditoriaIngresoRequest,
  ClaveActualizarRequest,
  Opcion,
  OpcionActualizarRequest,
  OpcionCrearRequest,
  OpcionMenu,
  OpcionPerfil,
  OpcionPerfilAsignarRequest,
  Perfil,
  ResultadoOperacion,
  UsuarioAsegurado,
  UsuarioClaveResetearRequest,
} from '../../../models/core/seguridad/adm-seguridad.model';

@Injectable({ providedIn: 'root' })
export class AdmSeguridadService {
  private readonly baseUrl = 'sw-core/api/adm-seguridad';
  private readonly http = inject(HttpGenericService);

  // ---------------------------------------------------------------------
  // Usuarios / Contraseñas
  // ---------------------------------------------------------------------

  /** Resetea la contraseña temporal y la envía al correo del usuario registrado. (requiere token) */
  resetearClave(request: UsuarioClaveResetearRequest): Observable<ResultadoOperacion> {
    return this.http.post<ResultadoOperacion, UsuarioClaveResetearRequest>(
      `${this.baseUrl}/usuarios/reseteo-clave`,
      request,
    );
  }

  /** Modifica la contraseña definitiva del usuario en sesión. */
  actualizarClave(request: ClaveActualizarRequest): Observable<ResultadoOperacion> {
    return this.http.put<ResultadoOperacion, ClaveActualizarRequest>(`${this.baseUrl}/usuarios/clave`, request);
  }

  /**
   * Consulta un usuario interno tratado como asegurado. (requiere token de sesión o token PUBLIC)
   *
   * Es el primer paso de la cascada de recuperación de clave: si devuelve una
   * lista vacía, hay que consultar `consultarAseguradoPorIdentificacion`.
   */
  consultarUsuarioAsegurado(codigo: string): Observable<UsuarioAsegurado[]> {
    return this.http
      .get<UsuarioAsegurado[]>(`${this.baseUrl}/asegurados/usuarios/${encodeURIComponent(codigo)}`)
      .pipe(catchError((error) => this.listaVaciaSiNoEncontrado<UsuarioAsegurado>(error, 'usuario interno')));
  }

  /** Consulta los asegurados externos por número de identificación. (requiere token de sesión o token PUBLIC) */
  consultarAseguradoPorIdentificacion(identificacion: string): Observable<AseguradoCliente[]> {
    return this.http
      .get<AseguradoCliente[]>(`${this.baseUrl}/asegurados/identificaciones/${encodeURIComponent(identificacion)}`)
      .pipe(catchError((error) => this.listaVaciaSiNoEncontrado<AseguradoCliente>(error, 'asegurado externo')));
  }

  // ---------------------------------------------------------------------
  // Auditoría de ingreso
  // ---------------------------------------------------------------------

  /** Registra el ingreso de un usuario en la bitácora. Devuelve el código generado. */
  registrarAuditoriaIngreso(request: AuditoriaIngresoRequest): Observable<ResultadoOperacion> {
    return this.http.post<ResultadoOperacion, AuditoriaIngresoRequest>(`${this.baseUrl}/auditorias/ingresos`, request);
  }

  // ---------------------------------------------------------------------
  // Menú del usuario  (reemplaza getMenuDiamante)
  // ---------------------------------------------------------------------

  /**
   * Consulta la lista plana y ordenada de opciones habilitadas para un usuario.
   * El armado del árbol que consume el sidenav lo resuelve `MenuAngularBuilder`.
   */
  consultarMenuPorUsuario(usuario: string): Observable<OpcionMenu[]> {
    return this.http
      .get<OpcionMenu[]>(`${this.baseUrl}/menus/usuarios/${encodeURIComponent(usuario)}`)
      .pipe(catchError((error) => this.listaVaciaSiNoEncontrado<OpcionMenu>(error, 'menú del usuario')));
  }

  // ---------------------------------------------------------------------
  // Opciones  (reemplaza getOpcionesbyPadre / createOpcion / updateOpcion / eliminarOpcion)
  // ---------------------------------------------------------------------

  /** Consulta las opciones hijas de una opción padre. `padre = 0` lista las raíces. */
  consultarOpcionesPorPadre(padre: number): Observable<Opcion[]> {
    return this.http
      .get<Opcion[]>(`${this.baseUrl}/opciones/padres/${padre}`)
      .pipe(catchError((error) => this.listaVaciaSiNoEncontrado<Opcion>(error, 'opciones por padre')));
  }

  /** Registra una nueva opción y la asigna automáticamente al perfil administrador. */
  crearOpcion(request: OpcionCrearRequest): Observable<ResultadoOperacion> {
    return this.http.post<ResultadoOperacion, OpcionCrearRequest>(`${this.baseUrl}/opciones`, request);
  }

  /** Actualiza la descripción, el icono y la ruta de una opción existente. */
  actualizarOpcion(codigo: number, request: OpcionActualizarRequest): Observable<ResultadoOperacion> {
    return this.http.put<ResultadoOperacion, OpcionActualizarRequest>(`${this.baseUrl}/opciones/${codigo}`, request);
  }

  /** Da de baja lógica una opción del menú. */
  eliminarOpcion(codigo: number): Observable<ResultadoOperacion> {
    return this.http.delete<ResultadoOperacion>(`${this.baseUrl}/opciones/${codigo}`);
  }

  // ---------------------------------------------------------------------
  // Perfiles  (reemplaza getPerfiles)
  // ---------------------------------------------------------------------

  /** Consulta el catálogo completo de perfiles de seguridad. */
  consultarPerfiles(): Observable<Perfil[]> {
    return this.http
      .get<Perfil[]>(`${this.baseUrl}/perfiles`)
      .pipe(catchError((error) => this.listaVaciaSiNoEncontrado<Perfil>(error, 'catálogo de perfiles')));
  }

  // ---------------------------------------------------------------------
  // Opciones por perfil  (reemplaza getOpcionesPerfil / asignarOpcionPerfil)
  // ---------------------------------------------------------------------

  /**
   * Consulta las opciones hijas de un padre con la bandera `asignada` ya resuelta
   * por el SP, evitando el cruce en memoria que hacía el core antiguo.
   */
  consultarOpcionesPorPerfil(padre: number, perfil: string): Observable<OpcionPerfil[]> {
    return this.http
      .get<OpcionPerfil[]>(`${this.baseUrl}/opciones-perfil/padres/${padre}/perfiles/${encodeURIComponent(perfil)}`)
      .pipe(catchError((error) => this.listaVaciaSiNoEncontrado<OpcionPerfil>(error, 'opciones por perfil')));
  }

  /** Asigna o actualiza la asignación de una opción a un perfil. */
  asignarOpcionPerfil(request: OpcionPerfilAsignarRequest): Observable<ResultadoOperacion> {
    return this.http.put<ResultadoOperacion, OpcionPerfilAsignarRequest>(`${this.baseUrl}/opciones-perfil`, request);
  }

  // ---------------------------------------------------------------------

  /**
   * Traduce el 404 de sw-core a una lista vacía. Cualquier otro error se propaga
   * para que el componente lo informe.
   */
  private listaVaciaSiNoEncontrado<T>(error: unknown, contexto: string): Observable<T[]> {
    if (error instanceof HttpErrorResponse && error.status === 404) {
      console.info(`[adm-seguridad] Sin registros para ${contexto}`);
      return of([] as T[]);
    }
    console.error(`[adm-seguridad] Error consultando ${contexto}:`, error);
    return throwError(() => error);
  }
}
